/**
 * useAdminProvidersList Hook
 *
 * Fetches and manages provider list data for admin audit dashboard
 * Supports filtering by organization
 */

import { useMemo } from 'react';

import {
  useGetAdminGlobalProvidersQuery,
  useGetAdminOrganizationProvidersQuery,
} from '../../store/api';
import type { ProviderSummary } from '../../types';

interface UseAdminProvidersListOptions {
  /** Filter by specific organization ID */
  organizationId?: string;
}

interface UseAdminProvidersListResult {
  /** List of providers */
  providers: ProviderSummary[];
  /** Total number of providers */
  total: number;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether list is empty */
  isEmpty: boolean;
}

export const useAdminProvidersList = (
  options: UseAdminProvidersListOptions = {},
): UseAdminProvidersListResult => {
  const { organizationId } = options;

  // Use org-specific query if organizationId is provided, otherwise use global
  const globalQuery = useGetAdminGlobalProvidersQuery(undefined, {
    skip: !!organizationId,
  });

  const orgQuery = useGetAdminOrganizationProvidersQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId },
  );

  const { data, isLoading, error, refetch: refetchQuery } = organizationId ? orgQuery : globalQuery;

  const refetch = (): void => {
    void refetchQuery();
  };

  const providers = useMemo(() => data?.providers || [], [data]);
  const total = data?.total || providers.length;
  const isEmpty = !isLoading && providers.length === 0;

  return {
    providers,
    total,
    isLoading,
    error: error ? 'Failed to load providers list' : null,
    refetch,
    isEmpty,
  };
};

export default useAdminProvidersList;

